import { SxProps, Theme } from '@mui/material'
import {
  AXIS_HEADER_H,
  CELL_BORDER,
  CELL_RADIUS,
  GRID_COL_HEADER_BG,
  GRID_COL_HEADER_BORDER,
  HATCH_BACKGROUND,
  SLOT_MARKER_COLOR,
} from '../config/constants'

/**
 * Estilos que comparten la grilla principal y la vista frontal de pasillo.
 *
 * Las dos vistas dibujan la misma estructura —etiqueta vertical del eje,
 * cabeceras de columna pegadas, celdas de posición no configurada— y antes cada
 * una tenía su copia del `sx`. Con una sola definición no pueden desalinearse.
 */

/**
 * Marca de "posición con slots": triángulo negro en la esquina inferior derecha.
 *
 * Se construye con bordes en un elemento de 0×0 —el borde inferior degenera en
 * un triángulo con el ángulo recto abajo a la derecha— en vez de `clip-path`,
 * que necesitaría un nodo con tamaño real encima del contenido de la celda.
 *
 * La sombra clara es un halo de 1px: sin ella el negro se pierde sobre las
 * marcas oscuras de la paleta.
 */
export const slotMarkerSx = (size: number): SxProps<Theme> => ({
  position: 'absolute',
  bottom: 0,
  right: 0,
  width: 0,
  height: 0,
  borderBottom: `${size}px solid ${SLOT_MARKER_COLOR}`,
  borderLeft: `${size}px solid transparent`,
  filter: 'drop-shadow(0 0 1px rgba(255,255,255,0.9))',
  pointerEvents: 'none',
})

/**
 * Etiqueta vertical del eje de filas: PASILLO en la grilla, NIVEL en la vista
 * frontal. Una sola definición para que las dos vistas no se desalineen.
 */
export const axisLabelSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  writingMode: 'vertical-rl',
  transform: 'rotate(180deg)',
  px: 0.75,
  flexShrink: 0,
  color: 'text.secondary',
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: 2,
  textTransform: 'uppercase',
  userSelect: 'none',
}

export const gridStyles = {
  /** Columna derecha: rótulo COLUMNA fijo + área scrollable. */
  axisColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  } as SxProps<Theme>,

  /**
   * Rótulo COLUMNA. Se desplaza el ancho de la columna de chips para quedar
   * centrado sobre las celdas y no sobre la grilla completa.
   *
   * `paddingTop` sólo lo usa la vista de pasillo, donde el rótulo va pegado a la
   * barra de leyenda y necesita separarse de ella.
   */
  columnAxisLabel: (offsetPx: number, paddingTop = 0): SxProps<Theme> => ({
    display: 'block',
    textAlign: 'center',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    flexShrink: 0,
    pt: paddingTop,
    pb: 0.5,
    ml: `${offsetPx}px`,
  }),

  /** Celda vacía de la esquina superior izquierda; tapa el cruce de los dos ejes. */
  cornerCell: {
    position: 'sticky',
    top: 0,
    left: 0,
    zIndex: 30,
    bgcolor: GRID_COL_HEADER_BG,
    borderRadius: '2px',
    height: AXIS_HEADER_H,
  } as SxProps<Theme>,

  columnHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    bgcolor: GRID_COL_HEADER_BG,
    border: `1px solid ${GRID_COL_HEADER_BORDER}`,
    borderRadius: '2px',
    height: AXIS_HEADER_H,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'default',
  } as SxProps<Theme>,

  /**
   * Cruce sin entrada en la respuesta: para el usuario es lo mismo que una
   * posición no configurada, así que se dibuja con el rayado de la leyenda y no
   * con un gris propio.
   */
  unconfiguredCell: (size: number): SxProps<Theme> => ({
    width: size,
    height: size,
    borderRadius: CELL_RADIUS,
    border: CELL_BORDER,
    background: HATCH_BACKGROUND,
  }),
}
